/**
 * Identity for Core Web Vitals RUM — deliberately SEPARATE from RudderStack.
 *
 * The RudderStack anonymousId is not reused here on purpose. It is shared per-origin
 * with the LMS, so it already leaks a logged-in learner identity into campus-portal
 * traffic; binding performance samples to it would inherit that problem and make a
 * pure-technical dataset carry marketing identity. These ids exist only to group
 * metrics and are never sent to any third party.
 *
 *   visitor_id   localStorage  — stable across visits; answers "is this a repeat visitor
 *                                (warm cache) or a first paint?"
 *   session_id   sessionStorage — one browsing session in one tab.
 *   page_load_id memory only   — one page view. The unique key for upserts, because the
 *                                client re-sends the whole buffer on every hide.
 */

const VISITOR_KEY = "cwv_vid";
const SESSION_KEY = "cwv_sid";

/** crypto.randomUUID needs a secure context; Math.random is fine for a grouping key. */
function uuid(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* falls through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Storage throws in Safari private mode and when cookies are blocked — never let that break a page. */
function readOrCreate(store: "localStorage" | "sessionStorage", key: string): string | null {
  try {
    const s = window[store];
    const existing = s.getItem(key);
    if (existing) return existing;
    const fresh = uuid();
    s.setItem(key, fresh);
    return fresh;
  } catch {
    return null;
  }
}

export const getVisitorId = (): string | null => readOrCreate("localStorage", VISITOR_KEY);
export const getSessionId = (): string | null => readOrCreate("sessionStorage", SESSION_KEY);

const currentPath = () =>
  typeof window !== "undefined" ? window.location.pathname.slice(0, 512) : null;

/**
 * One id per page load.
 *
 * NOT regenerated on client-side (soft) navigation, and that is correct: web-vitals
 * measures the initial document load, so every metric belongs to that one load no matter
 * where the visitor clicks afterwards.
 *
 * It IS regenerated on a BFCache restore (startNewPageLoad), because web-vitals reports a
 * restore as a fresh set of metrics. Under the old id those restore values — a near-zero LCP
 * — would have upserted over the ORIGINAL load's rows.
 */
let pageLoadId = uuid();

/**
 * The path captured at page load — again not updated on soft navigation, so LCP is
 * always attributed to the page that actually produced it rather than to whatever
 * page the visitor happened to be on when the browser fired visibilitychange.
 */
let entryPath = currentPath();

export const getPageLoadId = (): string => pageLoadId;
export const getEntryPath = (): string | null => entryPath;

/** A BFCache restore is a new page view: new id, and the path the restored page is on. */
export function startNewPageLoad(): void {
  pageLoadId = uuid();
  entryPath = currentPath();
}

/**
 * mobile / desktop, matching how PSI splits its two reports.
 *
 * Callers must evaluate this ONCE per page load (the reporter does): it reads the viewport
 * width, and a window resized across 768px between beacons used to file one page load's
 * metrics under both devices.
 */
export function getDeviceType(): string {
  if (typeof window === "undefined") return "unknown";
  const coarse =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches;
  return coarse || window.innerWidth < 768 ? "mobile" : "desktop";
}
